/**
 * Workspace containers for Atlas Vivo (plan 3.1).
 * Each builder returns the props bundle AtlasVivoLayout expects for that tab —
 * keeps AppMainView from owning story catalogs inline.
 */
import React, { Suspense, lazy } from 'react';
import moment from 'moment';
import { NAVIGATION_PRESETS } from '../features/registry';
import { dateAtRailPosition } from '../features/pulseRail';
import { MODAL_LOADING_FALLBACK } from '../ModalsProvider';
import CityReplayPanel from '../design/atlasVivo/CityReplayPanel';
import { t } from '../i18n';

const DashboardInsights = lazy(() => import('../DashboardInsights'));
const ImportModal = lazy(() => import('../ImportModal'));
const DiaryStatsModal = lazy(() => import('../DiaryStatsModal'));
const GitHubContributionsModal = lazy(() => import('../GitHubContributionsModal'));
const AnniversaryModal = lazy(() => import('../AnniversaryModal'));
const MemoryBookModal = lazy(() => import('../MemoryBookModal'));

/** Atlas tab: lens presets + contextual selection panel. */
export function buildAtlasContainer({
  activeNavigationPresetKey,
  handleNavigationPresetSelect,
  atlasSelection,
  setAtlasSelection,
  setSelectedPlaceForTags,
}) {
  return {
    lensProps: {
      presets: NAVIGATION_PRESETS,
      activePresetKey: activeNavigationPresetKey,
      onSelectPreset: handleNavigationPresetSelect,
    },
    atlasContext: {
      selection: atlasSelection,
      onClose: () => setAtlasSelection(null),
      onEditTags: (place) => {
        setSelectedPlaceForTags(place);
        setAtlasSelection(null);
      },
    },
  };
}

/** Dia tab: calendar day stream. */
export function buildDiaContainer({
  calendarDate,
  selectedDayEvents,
  atlasVivoEventDays,
  selectCalendarDayOnly,
  handleCalendarSelectEvent,
}) {
  return {
    selectedDate: calendarDate,
    dayEvents: selectedDayEvents,
    eventDays: atlasVivoEventDays,
    onSelectDay: selectCalendarDayOnly,
    onSelectEvent: handleCalendarSelectEvent,
  };
}

/** Histórias tab: narrative panels (insights, memory book, diary, anniversary, replay). */
export function buildHistoriasContainer({
  workspaceView,
  activeStoryKey,
  setActiveStoryKey,
  insights,
  insightsLoadedData,
  homeBase,
  currentFilters,
  handleDashboardCardAction,
  handlePrintPeriodSummary,
  safeFetchedData,
  user,
  calendarDate,
  anniversaryItems,
  handleAnniversarySelect,
  handleAnniversaryDateChange,
  searchCities,
  handleCitySelect,
  cityReplay,
  setWorkspaceView,
}) {
  return {
    stories: [
      {
        key: 'insights',
        iconName: 'bar-chart',
        title: t('historias.story.insights.title'),
        description: t('historias.story.insights.desc'),
        renderPanel: ({ onBack }) => (
          <Suspense fallback={MODAL_LOADING_FALLBACK}>
            <DashboardInsights
              embedded
              insights={{
                ...insights,
                loadedData: insightsLoadedData,
                baseCity: homeBase.city,
                currentRange: {
                  startDate: currentFilters.startDate,
                  endDate: currentFilters.endDate,
                },
              }}
              onClose={onBack}
              onCardAction={handleDashboardCardAction}
              onPrintSummary={handlePrintPeriodSummary}
            />
          </Suspense>
        ),
      },
      {
        key: 'memory-book',
        iconName: 'book',
        title: t('historias.story.memoryBook.title'),
        description: t('historias.story.memoryBook.desc'),
        renderPanel: ({ onBack }) => (
          <Suspense fallback={MODAL_LOADING_FALLBACK}>
            <MemoryBookModal
              embedded
              data={safeFetchedData}
              user={user}
              onClose={onBack}
            />
          </Suspense>
        ),
      },
      {
        key: 'diary-stats',
        iconName: 'notebook-pen',
        title: t('historias.story.diaryStats.title'),
        description: t('historias.story.diaryStats.desc'),
        renderPanel: ({ onBack }) => (
          <Suspense fallback={MODAL_LOADING_FALLBACK}>
            <DiaryStatsModal
              embedded
              entries={safeFetchedData.diary}
              onClose={onBack}
            />
          </Suspense>
        ),
      },
      {
        key: 'anniversary',
        iconName: 'calendar-heart',
        title: t('historias.story.anniversary.title'),
        description: t('historias.story.anniversary.desc'),
        renderPanel: ({ onBack }) => (
          <Suspense fallback={MODAL_LOADING_FALLBACK}>
            <AnniversaryModal
              embedded
              selectedDate={calendarDate}
              items={anniversaryItems}
              onClose={onBack}
              onSelectItem={(item) => {
                handleAnniversarySelect(item);
                onBack();
              }}
              onChangeDate={handleAnniversaryDateChange}
            />
          </Suspense>
        ),
      },
      {
        key: 'city-replay',
        iconName: 'play',
        title: t('historias.story.cityReplay.title'),
        description: t('historias.story.cityReplay.desc'),
        renderPanel: () => (
          <CityReplayPanel
            onCitySearch={searchCities}
            onCitySelect={(city) => {
              handleCitySelect(city);
              setWorkspaceView('atlas');
            }}
            selectedCity={cityReplay?.city ?? null}
          />
        ),
      },
    ],
    activeStoryKey: workspaceView === 'historias' ? activeStoryKey : null,
    onActiveStoryChange: setActiveStoryKey,
  };
}

/** Dados tab: import/health + GitHub. */
export function buildDadosContainer({
  workspaceView,
  activeStoryKey,
  setActiveStoryKey,
  user,
  handleLogout,
  fetchDataFromBackend,
  currentFilters,
  pendingManualForm,
  setPendingManualForm,
  pendingImportFocus,
  setPendingImportFocus,
}) {
  return {
    stories: [
      {
        key: 'import-health',
        iconName: 'download',
        title: t('dados.panel.importHealth.title'),
        description: t('dados.panel.importHealth.desc'),
        renderPanel: ({ onBack }) => (
          <Suspense fallback={MODAL_LOADING_FALLBACK}>
            <ImportModal
              embedded
              onClose={() => {
                onBack();
                setPendingManualForm(false);
                setPendingImportFocus(null);
              }}
              user={user}
              onReload={() => fetchDataFromBackend(currentFilters)}
              onLogout={handleLogout}
              initialManualForm={pendingManualForm}
              initialFocusSection={pendingImportFocus}
              onFocusSectionConsumed={() => setPendingImportFocus(null)}
            />
          </Suspense>
        ),
      },
      {
        key: 'github',
        iconName: 'github',
        title: t('dados.panel.github.title'),
        description: t('dados.panel.github.desc'),
        renderPanel: ({ onBack }) => (
          <Suspense fallback={MODAL_LOADING_FALLBACK}>
            <GitHubContributionsModal
              embedded
              user={user}
              onClose={onBack}
            />
          </Suspense>
        ),
      },
    ],
    activeStoryKey: workspaceView === 'dados' ? activeStoryKey : null,
    onActiveStoryChange: setActiveStoryKey,
  };
}

/** Pulse rail chrome shared across Atlas/Dia. */
export function buildPulseRailContainer({
  atlasVivoActive,
  currentFilters,
  pulseRailModel,
  setRailHoverPosition,
  setRailFocusPosition,
  selectCalendarDayOnly,
  setRailWorkspaceRequest,
  railWorkspaceRequest,
}) {
  const pulseRailDays = atlasVivoActive && currentFilters.startDate && currentFilters.endDate
    ? moment(currentFilters.endDate).diff(moment(currentFilters.startDate), 'days') + 1
    : 0;
  return {
    model: pulseRailModel,
    summary: atlasVivoActive ? {
      rangeLabel: currentFilters.startDate && currentFilters.endDate
        ? `${moment(currentFilters.startDate).format('MMM YYYY').toUpperCase()} — ${moment(currentFilters.endDate).format('MMM YYYY').toUpperCase()}`
        : '',
      daysLabel: `${pulseRailDays} ${pulseRailDays === 1 ? 'dia' : 'dias'}`,
    } : null,
    onHoverPosition: (position) => {
      setRailHoverPosition(position);
    },
    onSelectPosition: (position) => {
      const date = dateAtRailPosition(
        position,
        currentFilters.startDate,
        currentFilters.endDate,
      );
      if (!date) return;
      setRailFocusPosition(position);
      setRailHoverPosition(null);
      selectCalendarDayOnly(date);
      setRailWorkspaceRequest((count) => count + 1);
    },
    workspaceRequest: railWorkspaceRequest,
  };
}
